import { c as _c } from "react/compiler-runtime";
/**
 * Node Execution Status Hook
 *
 * Provides execution status and styling for workflow nodes.
 * Used by node components (TriggerNode, ActionNode, LogicNode) to
 * visualize execution state during workflow runs.
 */

import { useRunExecutionStore } from "@/stores/runExecutionStore";
import type { NodeExecutionState, NodeExecutionStatus } from "@/types/runStream";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

/**
 * Style classes for node execution status
 */
export interface NodeExecutionStyles {
  /** Border color class */
  borderClass: string;
  /** Hover border color class (slightly darker) */
  hoverBorderClass: string;
  /** Ring (glow) class */
  ringClass: string;
  /** Shadow class */
  shadowClass: string;
  /** Animation class (e.g., pulse for running) */
  animationClass: string;
  /** Status badge background and text color */
  badgeClass: string;
  /** Icon color class */
  iconColorClass: string;
}

/**
 * Style mappings for each execution status
 */
const RUNNING_STYLES: NodeExecutionStyles = {
  borderClass: "border-blue-500",
  hoverBorderClass: "hover:border-blue-600",
  ringClass: "[outline:2px_solid_#3b82f6] outline-offset-2",
  shadowClass: "",
  animationClass: "animate-pulse",
  badgeClass: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  iconColorClass: "text-blue-500"
};
const STATUS_STYLES: Record<NodeExecutionStatus, NodeExecutionStyles> = {
  idle: {
    borderClass: "",
    hoverBorderClass: "hover:border-blue-500",
    ringClass: "",
    shadowClass: "",
    animationClass: "",
    badgeClass: "",
    iconColorClass: ""
  },
  running: RUNNING_STYLES,
  // Solana actions confirm in ~1-2s — same as running but without pulse animation
  confirming: {
    ...RUNNING_STYLES,
    animationClass: ""
  },
  waiting: {
    borderClass: "border-amber-500",
    hoverBorderClass: "hover:border-amber-600",
    ringClass: "[outline:2px_solid_#f59e0b] outline-offset-2",
    shadowClass: "",
    animationClass: "animate-pulse",
    badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    iconColorClass: "text-amber-500"
  },
  success: {
    borderClass: "border-green-500",
    hoverBorderClass: "hover:border-green-600",
    ringClass: "[outline:2px_solid_#22c55e] outline-offset-2",
    shadowClass: "",
    animationClass: "",
    badgeClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    iconColorClass: "text-green-500"
  },
  failure: {
    borderClass: "border-red-500",
    hoverBorderClass: "hover:border-red-600",
    ringClass: "[outline:2px_solid_#ef4444] outline-offset-2",
    shadowClass: "",
    animationClass: "",
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    iconColorClass: "text-red-500"
  },
  partial: {
    borderClass: "border-amber-500",
    hoverBorderClass: "hover:border-amber-600",
    ringClass: "[outline:2px_solid_#f59e0b] outline-offset-2",
    shadowClass: "",
    animationClass: "",
    badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    iconColorClass: "text-amber-500"
  },
  auto_swapping: {
    borderClass: "border-violet-500",
    hoverBorderClass: "hover:border-violet-600",
    ringClass: "[outline:2px_solid_#8b5cf6] outline-offset-2",
    shadowClass: "",
    animationClass: "animate-pulse",
    badgeClass: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    iconColorClass: "text-violet-500"
  },
  scheduled: {
    borderClass: "border-blue-400",
    hoverBorderClass: "hover:border-blue-500",
    ringClass: "[outline:2px_solid_#60a5fa] outline-offset-2",
    shadowClass: "",
    animationClass: "",
    badgeClass: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300",
    iconColorClass: "text-blue-400"
  },
  skipped: {
    borderClass: "border-zinc-400 dark:border-zinc-600",
    hoverBorderClass: "hover:border-zinc-500",
    ringClass: "[outline:2px_solid_#a1a1aa] outline-offset-2",
    shadowClass: "",
    animationClass: "opacity-60",
    badgeClass: "bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled",
    iconColorClass: "text-ds-text-disabled"
  }
};

/**
 * Get combined class string for execution status
 */
export function getExecutionStatusClasses(status: NodeExecutionStatus | null): string {
  if (!status || status === "idle") return "";
  const styles = STATUS_STYLES[status];
  return [styles.borderClass, styles.hoverBorderClass, styles.ringClass, styles.shadowClass, styles.animationClass].filter(Boolean).join(" ");
}

/**
 * Return type for useNodeExecutionStatus hook
 */
interface UseNodeExecutionStatusReturn {
  /** Current execution status */
  status: NodeExecutionStatus;
  /** Full node execution state */
  nodeState: NodeExecutionState | undefined;
  /** Style classes for the status */
  styles: NodeExecutionStyles;
  /** Combined class string for easy application */
  statusClasses: string;
  /** Whether the node is currently running */
  isRunning: boolean;
  /** Whether the node is currently waiting */
  isWaiting: boolean;
  /** Whether the node is scheduled for future execution */
  isScheduled: boolean;
  /** Whether the node has completed (success, failure, or skipped) */
  isComplete: boolean;
  /** Whether there's an active run */
  hasActiveRun: boolean;
  /** Current run ID (if any) */
  runId: string | undefined;
}

/**
 * Hook for getting node execution status and styling.
 *
 * @param nodeId - The ID of the node to get status for
 * @returns Execution status, styles, and helper flags
 *
 * @example
 * ```tsx
 * function MyNode({ id }: { id: string }) {
 *   const { status, styles, statusClasses, isRunning } = useNodeExecutionStatus(id);
 *
 *   return (
 *     <div className={cn("base-classes", statusClasses)}>
 *       {isRunning && <Spinner />}
 *       {status !== "idle" && (
 *         <Badge className={styles.badgeClass}>{status}</Badge>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useNodeExecutionStatus(nodeId, parentForEachNodeId) {
  const $ = _c(17);
  let t0;
  if ($[0] !== nodeId) {
    t0 = state => {
      const nodeState = state.currentRun?.nodeStates[nodeId];
      return {
        status: (nodeState?.status ?? "idle") as NodeExecutionStatus,
        nodeState,
        hasActiveRun: state.currentRun !== null,
        runId: state.currentRun?.runId
      };
    };
    $[0] = nodeId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    status: rawStatus,
    nodeState: nodeState_0,
    hasActiveRun,
    runId
  } = useRunExecutionStore(useShallow(t0));
  let t1;
  if ($[2] !== parentForEachNodeId) {
    t1 = state_0 => {
      if (!parentForEachNodeId) {
        return;
      }
      return state_0.currentRun?.nodeStates[parentForEachNodeId]?.status;
    };
    $[2] = parentForEachNodeId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const parentForEachStatus = useRunExecutionStore(useShallow(t1));
  const status = rawStatus === "idle" && parentForEachStatus === "skipped" ? "skipped" : rawStatus;
  let t2;
  t2 = STATUS_STYLES[status];
  const styles = t2;
  let t3;
  let t4;
  if ($[4] !== status) {
    t4 = getExecutionStatusClasses(status);
    $[4] = status;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  t3 = t4;
  const statusClasses = t3;
  const t5 = status === "running" || status === "confirming";
  const t6 = status === "waiting";
  const t7 = status === "scheduled";
  const t8 = status === "success" || status === "partial" || status === "failure" || status === "skipped";
  let t9;
  if ($[6] !== hasActiveRun || $[7] !== nodeState_0 || $[8] !== runId || $[9] !== status || $[10] !== statusClasses || $[11] !== styles || $[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = {
      status,
      nodeState: nodeState_0,
      styles,
      statusClasses,
      isRunning: t5,
      isWaiting: t6,
      isScheduled: t7,
      isComplete: t8,
      hasActiveRun,
      runId
    };
    $[6] = hasActiveRun;
    $[7] = nodeState_0;
    $[8] = runId;
    $[9] = status;
    $[10] = statusClasses;
    $[11] = styles;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}

/**
 * Get display label for execution status
 */
export function getStatusLabel(status: NodeExecutionStatus): string {
  switch (status) {
    case "idle":
      return "";
    case "running":
      return "Running...";
    case "confirming":
      return "Confirming...";
    case "waiting":
      return "Waiting...";
    case "auto_swapping":
      return "Swapping funds...";
    case "scheduled":
      return "Scheduled";
    case "success":
      return "Success";
    case "partial":
      return "Partial";
    case "failure":
      return "Failed";
    case "skipped":
      return "Skipped";
    default:
      return "";
  }
}